import React from 'react'
import { createRoot } from 'react-dom/client'
import { Buffer } from 'buffer'
import { Amplify } from 'aws-amplify'
import {
  ApolloClient,
  InMemoryCache,
  ApolloProvider,
  split,
} from '@apollo/client'
import { createUploadLink } from 'apollo-upload-client'
import { GraphQLWsLink } from '@apollo/client/link/subscriptions'
import { createClient } from 'graphql-ws'
import { setContext } from '@apollo/client/link/context'
import { onError } from '@apollo/client/link/error'
import { Chart as ChartJS, ArcElement } from 'chart.js'

import Application from './Application'
import { ErrorBoundary } from './components/ErrorBoundary'
import * as serviceWorkerRegistration from './serviceWorkerRegistration'
import awsConfig from './aws-exports'
import env from '@app/environment'
import { getMainDefinition } from '@apollo/client/utilities'
import { i18nReady } from '@app/libraries/i18n'
import { PHProvider } from './providers/PostHogProvider'
import { FirebaseProvider } from './providers/FirebaseProvider'
import { InstallAppProvider } from './providers/InstallAppProvider'
import { getToken } from './helpers/auth'
import { handleUnauthenticated, hasUnauthenticatedError } from './helpers/unauthenticated'
import { ensurePersistence, auth } from './firebase'
import { themeManager } from './helpers/theme'

Amplify.configure(awsConfig)

ChartJS.register(ArcElement)

const themeReady = themeManager.init()


const httpLink = createUploadLink({
  uri: env.API_URL,
})

const httpLinkImageUpload = createUploadLink({
  uri: env.API_URL_IMAGE_UPLOAD,
})

const imageUploadLinkSplit = split(
  (operation) => operation.getContext().operation === 'imageUpload',
  httpLinkImageUpload,
  httpLink
)

const wsLink = new GraphQLWsLink(
  createClient({
    url: env.SUBSCRIPTION_URL,
    connectionParams: async () => {
      if(auth) await ensurePersistence()
      const token = await getToken()
      return { authToken: token }
    },
  })
)

const authLink = setContext(async (_, { headers }) => {
  if(auth) await ensurePersistence()
  try {
    const authType = (localStorage.getItem('auth_type') || '').trim().toLowerCase()
    const incognitoMode = sessionStorage.getItem('incognitoMode') ?? ''
    const token = await getToken()
    let tokenType = ''
    switch (authType) {
      case 'telegram':
        tokenType = 'Telegram'
        break
      case 'magic_link':
      case 'sso':
        tokenType = 'MagicLink'
        break
      case 'google':
        tokenType = 'Bearer'
        break
    }
    return {
      headers: {
        ...headers,
        authorization:  `${tokenType} ${token}`,
        'X-Incognito-Mode': incognitoMode,
      },
    }
  } catch (e) {
    return {
      headers: {
        ...headers,
      },
    }
  }
})

// Logs the user out when the backend keeps rejecting their token (see helpers/unauthenticated).
const unauthenticatedLink = onError(({ graphQLErrors }) => {
  if (hasUnauthenticatedError(graphQLErrors)) void handleUnauthenticated()
})

const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query)
    return (
      definition.kind === 'OperationDefinition' &&
      definition.operation === 'subscription'
    )
  },
  wsLink,
  unauthenticatedLink.concat(authLink).concat(imageUploadLinkSplit)
)

const client = new ApolloClient({
  link: splitLink,
  cache: new InMemoryCache({
    typePolicies: {
      TenantConfig: {
        keyFields: false,
        merge: true,
      },
      TenantIntegrationConfig: {
        keyFields: false,
        merge: true,
      },
      TenantBookie: {
        keyFields: false,
      },
    },
  }),
})

const container = document.getElementById('root')
const root = createRoot(container)

// Wait for the tenant's forced color-scheme (dark_mode feature flag) to
// resolve before the first paint, so there's no flash of the wrong theme.
Promise.all([ themeReady, i18nReady.catch(() => undefined) ]).then(() => {
  root.render(
    <React.StrictMode>
      <PHProvider>
        <ErrorBoundary feature="app_root">
          <FirebaseProvider>
            <ApolloProvider client={client}>
              <InstallAppProvider>
                <Application />
              </InstallAppProvider>
            </ApolloProvider>
          </FirebaseProvider>
        </ErrorBoundary>
      </PHProvider>
    </React.StrictMode>
  )
})

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://cra.link/PWA
// serviceWorkerRegistration.unregister()

serviceWorkerRegistration.register()

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
// reportWebVitals()
